import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Table, Form, Alert } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPlus } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath, CLIENT_TYPES } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 1200px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledTable = styled(Table)`
  background: white;
  border-radius: 8px;
  overflow: hidden;

  th {
    background: ${colors.milkGlass400};
    border-bottom: none;
    font-weight: 600;
    color: ${colors.nightlyWoods700};
  }

  td {
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    &:hover {
      background: ${colors.milkGlass200};
    }
  }
`;

const ClientTypeTag = styled.span`
  background: ${colors.blue100};
  color: ${colors.blue700};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  text-transform: uppercase;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const ClientList = () => {
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();

  const getInitialSearch = () => {
    const params = new URLSearchParams(location.search);
    return params.get("search") || "";
  };

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(getInitialSearch);

  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);

    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (newSearch !== currentSearch) {
      history.replace({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" });
    }
  }, [searchTerm]);

  useEffect(() => {
    if (userLoading) return;

    if (!authUserData || authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }

    if (!hasFetched) {
      fetchClients();
    }
  }, [authUserData, userLoading, hasFetched]);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const response = await request("admin/clients", { method: "GET" });
      setClients(response.clients || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
      if (err?.status === 403) {
        setError("You don't have permission to access client management.");
      } else {
        setError("Failed to load clients. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (clientId) => {
    history.push({
      pathname: generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: clientId }),
      state: { listQuery: location.search },
    });
  };

  const handleNewClient = () => {
    history.push(generateRoutePath(constants.CLIENT_ADMIN_NEW_ROUTE_NAME));
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatClientType = (type) => {
    if (!type) return "-";
    const found = CLIENT_TYPES.find(t => t.value.toLowerCase() === type.toLowerCase());
    return found ? found.label : type;
  };

  if (userLoading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Clients</PageTitle>
        <Button
          onClick={handleNewClient}
          style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
        >
          <FontAwesomeIcon icon={faPlus} /> New Client
        </Button>
      </PageHeader>

      <SearchContainer>
        <Form.Control
          type="text"
          placeholder="Search clients by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </LoadingContainer>
      ) : (
        <StyledTable hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>State</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "40px" }}>
                  {searchTerm ? "No clients match your search." : "No clients found."}
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => (
                <tr key={client.id} onClick={() => handleRowClick(client.id)}>
                  <td><strong>{client.name}</strong></td>
                  <td><ClientTypeTag>{formatClientType(client.client_type)}</ClientTypeTag></td>
                  <td>{client.state || "-"}</td>
                  <td>{formatDate(client.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </StyledTable>
      )}
    </PageContainer>
  );
};
